@if (isLoading()) {
  <app-branded-loader [label]="'common.loading' | translate" />
} @else {
<div class="page">
  <!-- Hero -->
  <section class="relative mb-8 overflow-hidden rounded-3xl bg-slate-900 p-8 text-white shadow-xl md:p-12">
    <div class="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[color-mix(in_srgb,var(--region-accent)_30%,transparent)] blur-3xl" aria-hidden="true"></div>
    <div class="pointer-events-none absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-[color-mix(in_srgb,var(--region-accent)_14%,transparent)] blur-3xl" aria-hidden="true"></div>

    <div class="relative z-10">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p class="text-accent-bright mb-4 text-xs font-black uppercase tracking-[0.3em]">{{ 'dashboard.title' | translate }}</p>
          <h1 class="hero-title mb-6">
            {{ 'dashboard.hero.title' | translate }}<br /><span class="text-accent-bright">{{ 'dashboard.hero.subtitle' | translate }}</span>
          </h1>
          <p class="mb-8 max-w-md font-medium leading-relaxed text-slate-300">
            {{ 'dashboard.hero.description' | translate }}
          </p>

          <div class="flex flex-wrap gap-3">
            <a routerLink="/rankings" class="btn btn-primary">
              {{ 'dashboard.hero.cta.rankings' | translate }}
            </a>
            <a routerLink="/races" class="btn border border-white/15 bg-white/10 text-white hover:bg-white/20">
              {{ 'dashboard.hero.cta.races' | translate }}
            </a>
          </div>
        </div>

        <!-- Countdown Card (Point 1) -->
        <div class="lg:ml-auto w-full max-w-md">
          @if (nextRace(); as race) {
          <div
            class="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-2xl">
            <div class="flex items-center gap-4 mb-8">
              <div
                class="accent-solid w-12 h-12 rounded-2xl flex items-center justify-center">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 class="text-accent-bright text-xs font-black uppercase tracking-widest">{{ 'dashboard.nextRace.title' | translate }}</h2>
                <p class="text-xl font-black text-white truncate max-w-[200px]">{{ race.name }}</p>
              </div>
            </div>

            <div class="grid grid-cols-3 gap-4 mb-8">
              <div class="bg-white/5 rounded-2xl p-4 text-center border border-white/5">
                <span class="block text-3xl font-black text-white">{{ countdown().days }}</span>
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{{
                  'dashboard.nextRace.days' | translate }}</span>
              </div>
              <div class="bg-white/5 rounded-2xl p-4 text-center border border-white/5">
                <span class="block text-3xl font-black text-white">{{ countdown().hours }}</span>
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{{
                  'dashboard.nextRace.hours' | translate }}</span>
              </div>
              <div class="bg-white/5 rounded-2xl p-4 text-center border border-white/5">
                <span class="block text-3xl font-black text-white">{{ countdown().minutes }}</span>
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{{
                  'dashboard.nextRace.minutes' | translate }}</span>
              </div>
            </div>

            <a [routerLink]="['/race-detail', race.id]"
              class="btn w-full border border-white/15 bg-white/10 text-white hover:bg-white/20">
              {{ 'dashboard.nextRace.view' | translate }}
            </a>
          </div>
          } @else {
          <!-- Simple stats if no upcoming race -->
          <div class="grid grid-cols-2 gap-6">
            <div class="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
              <span class="text-xs font-black text-slate-400 uppercase block mb-2">{{ 'dashboard.stats.totalRaces' |
                translate }}</span>
              <span class="text-5xl font-black text-white">{{ totalRaces() }}</span>
            </div>
            <div class="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
              <span class="text-xs font-black text-slate-400 uppercase block mb-2">{{ 'dashboard.stats.totalDistance' |
                translate }}</span>
              <span class="text-5xl font-black text-white">{{ totalDistance() | distance }} <span class="text-sm">km</span></span>
            </div>
          </div>
          }
        </div>
      </div>
    </div>
  </section>

  <div>

    <!-- Summary of the events listed below: real counts only -->
    <section class="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16" [attr.aria-label]="'dashboard.summary.title' | translate">
      <div class="card p-8">
        <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">{{ 'dashboard.summary.events' | translate }}</span>
        <span class="text-4xl font-black text-slate-900 dark:text-white">{{ pastEvents().length }}</span>
      </div>
      <div class="card p-8">
        <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">{{ 'dashboard.summary.races' | translate }}</span>
        <span class="text-4xl font-black text-slate-900 dark:text-white">{{ totalRaceCount() }}</span>
      </div>
      <div class="card p-8">
        <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">{{ 'dashboard.summary.participations' | translate }}</span>
        <span class="text-4xl font-black text-slate-900 dark:text-white">{{ totalParticipations() }}</span>
      </div>
    </section>

    <!-- Live Activity Feed Section -->
    <div class="space-y-12">
      <div class="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-6 mb-12">
        <h2 class="section-title flex items-center gap-3 text-2xl">
          <span
            class="accent-solid w-12 h-12 rounded-2xl flex items-center justify-center">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </span>
          {{ 'dashboard.liveFeed.title' | translate }}
        </h2>
        <div class="flex items-center gap-2">
          <span class="relative flex h-3 w-3">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span class="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
          </span>
          <span class="text-xs font-black text-slate-400 uppercase tracking-widest">{{ 'dashboard.liveFeed.status' | translate }}</span>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-16">
        @for (race of pastEvents(); track race.id) {
        <div [routerLink]="['/race-detail', race.id]"
          class="card group relative cursor-pointer overflow-hidden transition-all duration-500 hover:shadow-xl">

          <div class="flex flex-col lg:flex-row min-h-[500px]">

            <!-- Expanded Image Column -->
            <div class="lg:w-2/5 relative h-80 lg:h-auto overflow-hidden">
              @if (race.imageUrl) {
              <img [src]="race.imageUrl" [alt]="race.name"
                class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105">
              } @else {
              <img src="/assets/race-default-trail.webp" [alt]="race.name"
                class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105">
              }

              <div class="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>

              <div class="absolute bottom-8 left-8">
                <span
                  class="accent-solid px-5 py-2 text-xs font-black uppercase tracking-widest rounded-full">
                  {{ 'dashboard.liveFeed.imported' | translate }}
                </span>
              </div>
            </div>

            <!-- Rich Content Column -->
            <div class="lg:w-3/5 p-10 lg:p-14 flex flex-col">
              <div class="flex flex-col sm:flex-row sm:items-start justify-between gap-6 mb-10">
                <div>
                  <div class="flex items-center gap-3 mb-4">
                    <div class="p-2 rounded-xl bg-slate-100 dark:bg-slate-800">
                      <svg class="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002-2z" />
                      </svg>
                    </div>
                    <span class="text-xs font-black text-accent uppercase tracking-[0.2em]">{{
                      formatDate(race.date) }}</span>
                  </div>
                  <h3
                    class="text-2xl lg:text-3xl font-black text-slate-900 dark:text-slate-100 tracking-tight leading-tight">
                    {{ race.name }}</h3>
                  <div class="flex items-center gap-3 mt-4 text-slate-500 dark:text-slate-400">
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <span class="text-lg font-bold">{{ race.location | displayValue }}</span>
                  </div>
                </div>
              </div>

              <!-- Quick Stats Grid -->
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-12">
                <div
                  class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <span class="text-[9px] font-black text-slate-400 uppercase block mb-1">{{ 'dashboard.performance.participants' | translate }}</span>
                  <span class="text-xl font-black text-slate-900 dark:text-white">{{ participantCounts().get(race.id)
                    || 0 }}</span>
                </div>
                <div
                  class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <span class="text-[9px] font-black text-slate-400 uppercase block mb-1">{{ 'dashboard.performance.distance' | translate }}</span>
                  <span class="text-xl font-black text-slate-900 dark:text-white">
                    @if (distanceRange(race); as range) {
                      {{ range.min | distance }}@if (range.max !== range.min) {–{{ range.max | distance }}} <span class="text-xs">km</span>
                    } @else {
                      <span class="text-sm text-slate-400">{{ null | displayValue }}</span>
                    }
                  </span>
                </div>
                <div
                  class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <span class="text-[9px] font-black text-slate-400 uppercase block mb-1">{{ 'dashboard.performance.races' | translate }}</span>
                  <span class="text-xl font-black text-slate-900 dark:text-white">{{ race.races?.length ?? 0 }}</span>
                </div>
                <div
                  class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <span class="text-[9px] font-black text-slate-400 uppercase block mb-1">{{ 'dashboard.performance.disciplines' | translate }}</span>
                  <span class="text-xl font-black text-slate-900 dark:text-white">{{ disciplineCount(race) }}</span>
                </div>
              </div>

              <!-- Winner of each of the largest races: results are only compared within a race -->
              @if (getWinners(race.id); as winners) {
              @if (winners.length > 0) {
              <div class="mt-auto">
                <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3">{{ 'dashboard.winners.title' | translate }}</span>
                <ul class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  @for (winner of winners; track winner.raceId) {
                  <li class="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
                    <span class="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-yellow-400 text-yellow-900 font-black text-sm" aria-hidden="true">1</span>
                    <span class="min-w-0">
                      @if (winner.humanId) {
                        <a [routerLink]="['/human-profile', winner.humanId]" (click)="$event.stopPropagation()"
                          class="relative z-10 block truncate text-sm font-black text-slate-900 dark:text-slate-100 hover:text-[color:var(--accent-logo)] hover:underline underline-offset-4 transition-colors">
                          {{ winner.humanName }} <app-registered-star [humanId]="winner.humanId" />
                        </a>
                      } @else {
                        <span class="block truncate text-sm font-black text-slate-900 dark:text-slate-100">{{ winner.humanName }}</span>
                      }
                      <span class="block truncate text-[11px] font-bold text-slate-500 dark:text-slate-400">
                        {{ winner.raceName }}@if (winner.distanceKm) { · {{ winner.distanceKm | distance }} km} · {{ formatTime(winner.timeSeconds) }}
                      </span>
                    </span>
                  </li>
                  }
                </ul>
              </div>
              }
              }
            </div>
          </div>
        </div>
        } @empty {
        <!-- Loading or Empty state -->
        <div class="py-24 text-center">
          <div
            class="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6 animate-pulse">
            <svg class="w-10 h-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <p class="text-sm font-black text-slate-400 uppercase tracking-widest">{{ 'dashboard.liveFeed.init' |
            translate }}</p>
        </div>
        }
      </div>
    </div>
  </div>
</div>
}
